import { IconCheck, IconX } from '@tabler/icons-react-native';
import { Gradient } from 'app/design/primitives/gradient';
import { Text } from 'app/design/primitives/text';
import { useTheme } from 'app/shopify';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, type ReactNode } from 'react';
import { StyleSheet, type StyleProp, type ViewStyle, useWindowDimensions } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';

export type ReviewSwipeAction = 'accept' | 'reject';
export interface SwipeableReviewCardHandle {
  swipe: (action: ReviewSwipeAction) => void;
}
export const REVIEW_SWIPE_THRESHOLD = 110;
export const REVIEW_SWIPE_VELOCITY_THRESHOLD = 850;

function useSwipeCompletion(resetKey: string, onAccept: () => void, onReject: () => void) {
  const completedKey = useRef<string | null>(null);
  const callbacks = useRef({ onAccept, onReject });
  useEffect(() => {
    callbacks.current = { onAccept, onReject };
  }, [onAccept, onReject]);
  // Native animation completion must use the latest callbacks without restarting the gesture.
  const completeSwipe = useCallback(
    (action: ReviewSwipeAction) => {
      if (completedKey.current === resetKey) return;
      completedKey.current = resetKey;
      if (action === 'accept') callbacks.current.onAccept();
      else callbacks.current.onReject();
    },
    [resetKey],
  );
  const resetCompletion = useCallback(() => {
    completedKey.current = null;
  }, []);
  return { completeSwipe, resetCompletion };
}

export const SwipeableReviewCard = forwardRef<
  SwipeableReviewCardHandle,
  {
    acceptLabel: string;
    rejectLabel: string;
    canAccept: boolean;
    disabled: boolean;
    onAccept: () => void;
    onReject: () => void;
    resetKey: string;
    children: ReactNode;
    showOverlays?: boolean;
    overlayVariant?: 'badge' | 'gradient';
    canReject?: boolean;
    maxRotation?: number;
    verticalDragFactor?: number;
    onDirectionChange?: (direction: ReviewSwipeAction | null) => void;
    style?: StyleProp<ViewStyle>;
  }
>(function SwipeableReviewCardComponent(
  {
    acceptLabel,
    rejectLabel,
    canAccept,
    disabled,
    onAccept,
    onReject,
    resetKey,
    children,
    showOverlays = true,
    overlayVariant = 'badge',
    canReject = true,
    maxRotation = 12,
    verticalDragFactor = 0.45,
    onDirectionChange,
    style,
  },
  ref,
) {
  const theme = useTheme();
  const { width } = useWindowDimensions();
  const reducedMotion = useReducedMotion();
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const isLeaving = useSharedValue(false);
  const direction = useSharedValue(0);
  const wasDisabled = useRef(disabled);
  const { completeSwipe, resetCompletion } = useSwipeCompletion(resetKey, onAccept, onReject);
  // Keep gesture and imperative callbacks stable while native animations are active.
  const reset = useCallback(() => {
    isLeaving.set(false);
    translateX.set(0);
    translateY.set(0);
    direction.set(0);
    resetCompletion();
    onDirectionChange?.(null);
  }, [direction, isLeaving, onDirectionChange, resetCompletion, translateX, translateY]);
  useEffect(reset, [resetKey, reset]);
  useEffect(() => {
    if (wasDisabled.current && !disabled) reset();
    wasDisabled.current = disabled;
  }, [disabled, reset]);
  const reportDirection = useCallback(
    (value: number) => {
      onDirectionChange?.(value > 0 ? 'accept' : value < 0 ? 'reject' : null);
    },
    [onDirectionChange],
  );
  const launch = useCallback(
    (action: ReviewSwipeAction) => {
      'worklet';
      if (disabled || isLeaving.value || (action === 'accept' && !canAccept) || (action === 'reject' && !canReject))
        return;
      isLeaving.set(true);
      const sign = action === 'accept' ? 1 : -1;
      scheduleOnRN(reportDirection, sign);
      translateX.set(
        withTiming(sign * Math.max(520, width + 100), { duration: reducedMotion ? 0 : 220 }, (finished) => {
          if (finished) scheduleOnRN(completeSwipe, action);
        }),
      );
    },
    [canAccept, canReject, completeSwipe, disabled, isLeaving, reducedMotion, reportDirection, translateX, width],
  );
  useImperativeHandle(ref, () => ({ swipe: launch }), [launch]);
  const pan = useMemo(
    () =>
      Gesture.Pan()
        .enabled(!disabled)
        .activeOffsetX([-18, 18])
        .failOffsetY([-18, 18])
        .onUpdate((event) => {
          if (isLeaving.value) return;
          translateX.set(event.translationX);
          translateY.set(reducedMotion ? 0 : event.translationY * verticalDragFactor);
          const next = event.translationX > 18 && canAccept ? 1 : event.translationX < -18 && canReject ? -1 : 0;
          if (next !== direction.value) {
            direction.set(next);
            scheduleOnRN(reportDirection, next);
          }
        })
        .onEnd((event) => {
          if (isLeaving.value) return;
          const accept =
            canAccept &&
            (event.translationX > REVIEW_SWIPE_THRESHOLD || event.velocityX > REVIEW_SWIPE_VELOCITY_THRESHOLD);
          const reject =
            canReject &&
            (event.translationX < -REVIEW_SWIPE_THRESHOLD || event.velocityX < -REVIEW_SWIPE_VELOCITY_THRESHOLD);
          if (accept || reject) {
            launch(accept ? 'accept' : 'reject');
            return;
          }
        })
        .onFinalize(() => {
          // A cancelled gesture must return home without making a decision.
          if (isLeaving.value) return;
          translateX.set(withSpring(0, { damping: 18, stiffness: 180 }));
          translateY.set(withSpring(0, { damping: 18, stiffness: 180 }));
          direction.set(0);
          scheduleOnRN(reportDirection, 0);
        }),
    [
      canAccept,
      canReject,
      direction,
      disabled,
      isLeaving,
      launch,
      reducedMotion,
      reportDirection,
      translateX,
      translateY,
      verticalDragFactor,
    ],
  );
  const cardStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: translateX.value },
      { translateY: translateY.value },
      {
        rotate: `${String(reducedMotion ? 0 : Math.max(-maxRotation, Math.min(maxRotation, translateX.value / 18)))}deg`,
      },
    ],
  }));
  const acceptStyle = useAnimatedStyle(() => ({
    opacity: canAccept ? Math.max(0, Math.min(1, translateX.value / REVIEW_SWIPE_THRESHOLD)) : 0,
  }));
  const rejectStyle = useAnimatedStyle(() => ({
    opacity: canReject ? Math.max(0, Math.min(1, -translateX.value / REVIEW_SWIPE_THRESHOLD)) : 0,
  }));
  return (
    <GestureDetector gesture={pan}>
      <Animated.View
        style={[
          styles.card,
          {
            backgroundColor: theme.colors['surface.default'],
            borderRadius: theme.borderRadii['radius.sm'],
            padding: theme.spacing['spacing.general.x3'],
          },
          cardStyle,
          style,
        ]}
      >
        {showOverlays && overlayVariant === 'gradient' ? (
          <>
            {(
              [
                {
                  action: 'accept',
                  color: '#B3EC6A',
                  badge: theme.colors['surface.success.default'],
                  label: acceptLabel,
                  Icon: IconCheck,
                  animatedStyle: acceptStyle,
                },
                {
                  action: 'reject',
                  color: '#FFA9AB',
                  badge: theme.colors['surface.error.default'],
                  label: rejectLabel,
                  Icon: IconX,
                  animatedStyle: rejectStyle,
                },
              ] as const
            ).map(({ action, color, badge, label, Icon, animatedStyle }) => (
              <Animated.View
                key={action}
                pointerEvents="none"
                aria-hidden
                accessibilityElementsHidden
                importantForAccessibility="no-hide-descendants"
                style={[StyleSheet.absoluteFill, { zIndex: 3 }, animatedStyle]}
              >
                <Gradient from={color} to={color} toOpacity={0} style={StyleSheet.absoluteFill} borderRadius={16} />
                <Animated.View
                  style={[
                    styles.gradientBadge,
                    { backgroundColor: badge, ...(action === 'accept' ? { left: 16 } : { right: 16 }) },
                  ]}
                >
                  <Icon size={20} color="#FFFFFF" />
                  <Text color="text.inverse.primary" fontWeight="600" variant="bodySmall">
                    {label}
                  </Text>
                </Animated.View>
              </Animated.View>
            ))}
          </>
        ) : showOverlays ? (
          <>
            <Animated.View
              pointerEvents="none"
              aria-hidden
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
              style={[styles.overlay, styles.accept, acceptStyle]}
            >
              <Text color="surface.default" fontWeight="700" variant="headerXSmall">
                {acceptLabel}
              </Text>
            </Animated.View>
            <Animated.View
              pointerEvents="none"
              aria-hidden
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
              style={[styles.overlay, styles.reject, rejectStyle]}
            >
              <Text color="surface.default" fontWeight="700" variant="headerXSmall">
                {rejectLabel}
              </Text>
            </Animated.View>
          </>
        ) : null}
        {children}
      </Animated.View>
    </GestureDetector>
  );
});
const styles = StyleSheet.create({
  gradientBadge: {
    position: 'absolute',
    top: 16,
    height: 40,
    borderRadius: 1000,
    paddingHorizontal: 16,
    gap: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  card: { shadowColor: '#000', shadowOffset: { height: 12, width: 0 }, shadowOpacity: 0.08, shadowRadius: 18 },
  overlay: {
    borderRadius: 8,
    borderWidth: 2,
    paddingHorizontal: 12,
    paddingVertical: 8,
    position: 'absolute',
    top: 24,
    zIndex: 3,
    borderColor: 'rgba(255,255,255,0.86)',
  },
  accept: { backgroundColor: 'rgba(24,140,112,0.88)', left: 20, transform: [{ rotate: '-10deg' }] },
  reject: { backgroundColor: 'rgba(211,47,47,0.88)', right: 20, transform: [{ rotate: '10deg' }] },
});
